/* Copyright (c) 2023-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { animationFrameScheduler, defer, interval, Observable, Subject } from 'rxjs';
import { endWith, filter, finalize, map, takeWhile, tap } from 'rxjs/operators';
import { linear } from './easing-function';

import { CustomScrollOption, EasingFunction } from './scrolling.types';

export const DefaultScrollDuration = 200;

function msElapsed(scheduler = animationFrameScheduler) {
    return defer(() => {
        const start = scheduler.now();
        return interval(0, scheduler).pipe(map(() => scheduler.now() - start));
    });
}

function duration(ms: number, scheduler = animationFrameScheduler) {
    return msElapsed(scheduler).pipe(
        map(ems => ems / ms),
        takeWhile(t => t < 1),
        endWith(1)
    );
}

/**
 * Please check this [ticket](https://jirasw.nvidia.com/browse/AVENGERS-2310) for a detailed explanation why we need to implement our own scrolling API.
 */
export class ScrollPlayer {
    // emit true on start and false on end
    private onScrollStatusChanged = new Subject<boolean>();
    private startX: number;
    private startY: number;
    private scrollAmountX: number;
    private scrollAmountY: number;
    private isComplete = false;
    private isStarted = false;
    private easingFunction: EasingFunction;
    private duration: number;

    /**
     * instantiate a scroll animation player. the constructor is equal to a passive "container.scrollTo(x, y)"
     * you need to explicitly call "play" function to start the scrolling animation.
     * but with the ability to control the speed, cancel the animation and provides observables to listen
     * the start and the end of the scrolling
     *
     * @example
     * const scrollerElement = document.getElementById('scroller');
     * const destination = {x: 100, y: 200};
     * const options = { duration: 500 };
     * const scrollPlayer = new ScrollPlayer(scrollerElement, destination.x, destination.y, options);
     * scrollPlayer.onScrollStart().pipe(take(1)).subscribe(() => { console.log('scroll animation starts'); });
     * scrollPlayer.onScrollEnd().pipe(take(1)).subscribe(() => { console.log('scroll animation ends'); });
     * scrollPlayer.play().subscribe();
     *
     * @param container the scroll container you wish to scroll
     * @param x the horizontal position you want to move your container's top left to at the end of the scrolling
     * @param y the vertical position you want to move your container's top left at the end of the scrolling
     * @param option.duration time needed for the scrolling from start to end
     * @param option.easingFunction an easing funtction that takes a percentage value from 0 - 1 and maps it to another
     * percentage value
     *
     */
    constructor(
        private container: HTMLElement,
        x: number,
        y: number,
        option?: CustomScrollOption
    ) {
        if (!container || !(container instanceof HTMLElement)) {
            throw new Error(`${container.tagName} is not a valid container`);
        }

        this.easingFunction = option?.easingFunction ?? linear;

        if (option?.duration && option.duration < 0) {
            throw new Error(`${option.duration} is not a valid duration. the duration must greater or equal than 0.`);
        }

        this.duration = option?.duration ?? DefaultScrollDuration;

        this.startX = container.scrollLeft;
        this.startY = container.scrollTop;

        this.scrollAmountX = x - this.startX;
        this.scrollAmountY = y - this.startY;
    }

    onScrollStart(): Observable<boolean> {
        return this.onScrollStatusChanged.pipe(filter(status => status));
    }

    onScrollEnd(): Observable<boolean> {
        return this.onScrollStatusChanged.pipe(filter(status => !status));
    }

    // emit the current scrolling progress in percentage
    start(): Observable<number> {
        if (this.isComplete || this.isStarted) {
            throw new Error(`the scrolling is ${this.isComplete ? 'completed' : 'started'}.`);
        }

        return duration(this.duration).pipe(
            tap(() => {
                if (!this.isStarted) {
                    this.isStarted = true;
                    this.onScrollStatusChanged.next(true);
                }
            }),
            map(progress => this.easingFunction(progress)),
            map(easingFactor => {
                this.container.scrollLeft = this.startX + this.scrollAmountX * easingFactor;
                this.container.scrollTop = this.startY + this.scrollAmountY * easingFactor;
                return easingFactor;
            }),
            finalize(() => {
                this.isComplete = true;
                this.onScrollStatusChanged.next(false);
            })
        );
    }
}
